import { AlertTriangle, Tags } from 'lucide-react';

import { RestaurantRawMaterialDTO } from '../../../../types/restaurant/RestaurantRawMaterialTypes';
import RestaurantTable, {
  RestaurantTableColumn,
} from '../../shared/RestaurantTable';
import RestaurantTableActions from '../../shared/RestaurantTableActions';

interface Props {
  materials: RestaurantRawMaterialDTO[];
  onIdentifiers: (material: RestaurantRawMaterialDTO) => void;
  onEdit: (material: RestaurantRawMaterialDTO) => void;
  onToggleActive: (material: RestaurantRawMaterialDTO) => void;
}

const formatStock = (value: number | null, unitCode: string) =>
  `${Number(value ?? 0).toLocaleString('es-SV', {
    maximumFractionDigits: 6,
  })} ${unitCode || ''}`.trim();

const formatMoney = (value: number | null) =>
  new Intl.NumberFormat('es-SV', {
    style: 'currency',
    currency: 'USD',
  }).format(value ?? 0);

const RawMaterialTable = ({
  materials,
  onIdentifiers,
  onEdit,
  onToggleActive,
}: Props) => {
  const columns: RestaurantTableColumn<RestaurantRawMaterialDTO>[] = [
    {
      key: 'code',
      header: 'Código',
      className: 'whitespace-nowrap font-semibold text-primary',
      cell: (material) => material.code,
    },
    {
      key: 'name',
      header: 'Nombre',
      className: 'font-medium text-black dark:text-white',
      cell: (material) => material.name,
    },
    {
      key: 'category',
      header: 'Categoría',
      className: 'text-sm text-bodydark2',
      cell: (material) => material.categoryName,
    },
    {
      key: 'currentStock',
      header: 'Stock actual',
      className: 'whitespace-nowrap',
      cell: (material) => (
        <span
          className={`inline-flex items-center gap-1.5 font-semibold ${
            material.active && material.lowStock
              ? 'text-meta-8 dark:text-warning'
              : 'text-black dark:text-white'
          }`}
        >
          {material.active && material.lowStock && (
            <AlertTriangle className="h-3.5 w-3.5" />
          )}
          {formatStock(material.currentStock, material.unitCode)}
        </span>
      ),
    },
    {
      key: 'minimumStock',
      header: 'Stock mínimo',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (material) => formatStock(material.minimumStock, material.unitCode),
    },
    {
      key: 'cost',
      header: 'Costo unitario',
      className: 'whitespace-nowrap font-semibold text-black dark:text-white',
      cell: (material) => formatMoney(material.cost),
    },
    {
      key: 'supplier',
      header: 'Proveedor',
      className: 'max-w-[180px] truncate text-sm text-bodydark2',
      cell: (material) => material.supplierName || 'Sin proveedor',
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (material) =>
        material.active && material.lowStock ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/15 px-2.5 py-1 text-xs font-semibold text-meta-8 dark:text-warning">
            <AlertTriangle className="h-3.5 w-3.5" /> Stock bajo
          </span>
        ) : (
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
              material.active
                ? 'border-success/20 bg-success/10 text-success'
                : 'border-danger/20 bg-danger/10 text-danger'
            }`}
          >
            {material.active ? 'Disponible' : 'Inactiva'}
          </span>
        ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      headClassName: 'text-right',
      cell: (material) => (
        <RestaurantTableActions
          onEdit={() => onEdit(material)}
          onToggle={() => onToggleActive(material)}
          active={material.active}
          toggleLabel={{ on: 'Desactivar', off: 'Reactivar' }}
          extra={
            <button
              type="button"
              onClick={() => onIdentifiers(material)}
              className="inline-flex items-center gap-2 rounded-xl border border-stroke px-3 py-2 text-sm font-medium transition hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-strokedark"
            >
              <Tags className="h-4 w-4" /> Identificadores
            </button>
          }
        />
      ),
    },
  ];

  return (
    <RestaurantTable
      columns={columns}
      rows={materials}
      rowKey={(material) => material.id}
      rowClassName={(material) =>
        [
          material.active ? '' : 'opacity-65',
          material.active && material.lowStock ? 'bg-warning/5' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
      minWidth="min-w-[1200px]"
    />
  );
};

export default RawMaterialTable;
